import {
  Button,
  Combobox,
  DatePicker,
  Divider,
  Flex,
  HelpText,
  Link,
  Notice,
  Select,
  Text,
  TextInput,
  TextInputProps,
  createForm,
} from '@applyboard/crystal-ui'
import { DeleteOutlineIcon, PlusOutlineIcon } from '@applyboard/ui-icons'
import {
  EducationHistory,
  FileData,
  EducationLevel as LevelOfEducation,
} from '@applyboard/school-applications-types-lib'
import { get, isEmpty, set } from 'lodash'
import { nanoid } from 'nanoid'
import { useEffect, useState, useRef, ComponentType } from 'react'
import { SetAllFieldsOptional } from '@applyboard/school-backend-lib'
import {
  COUNTRY_NAMES,
  CountryIsoCode,
} from '@applyboard/school-backend-lib/dist/common-types/country'
import { RawApplicationResponse, useGetSchool, useUpdateApplication } from '../../../hooks'
import { convertTimelessDateStrToLocalDate } from '../../../utils/convertTimelessDateStrToLocalDate'
import { DocumentTags, EducationLevel } from '../../../utils/enums'
import { Asterisk } from '../../Asterisk'
import { StudentApplication } from '../types'
import { useApplicationFormContext } from './ApplicationForm'
import { FileUploadField } from './FileUploadField'
import { getFilesOfType } from './utils'
import { GenericError } from '../../../utils'
import { SpecificField } from '../SpecificField/SpecificField'
import { useGetApplicationPageProps } from '../../../hooks/useGetApplicationPageProps'
import { Student, useGetStudent } from '../../../hooks/useGetStudent'
import { Loading } from '../../Loading'
import { useUpdateStudent } from '../../../hooks/useUpdateStudent'
import { useQueryClient } from '@tanstack/react-query'
import { specificFieldValidations } from '../../../utils/specificFieldValidations'
import { StudentSchoolInformation } from '@backend/resources/student/types'
import {
  YesNo,
  convertBooleanToYesNoEmpty,
  convertYesNoEmptyToBoolOrNull,
} from '../../../utils/convertBooleanToString'

type EducationalAccomplishment = {
  id: string
  educationLevel: string
  country: string
  schoolName: string
  credentialReceived: string
  startDate: string
  endDate: string
  gradeSchemeId: string
  transcriptDocuments: Array<{
    id: string
    file: File
  }>
  certificateDocuments: Array<{
    id: string
    file: File
  }>
}

type EducationHistoryFormFields = {
  haveAttendedSchool: string
  schoolAssignedStudentId: string
  ontarioEducationNumber: string
  albertaStudentNumber: string
  personalEducationNumber: string
  educationHistory: EducationalAccomplishment[]
}

const UPLOAD_LIMIT = 1

const { Form, Field, useFieldArray, useFieldValues, useSetFieldValues } =
  createForm<EducationHistoryFormFields>()

type EducationHistoryTabProps = Readonly<{
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: GenericError) => void
  updateApplication: ReturnType<typeof useUpdateApplication>['updateApplication']
  formId: string
}>

export function EducationHistoryTab(props: EducationHistoryTabProps) {
  const queryClient = useQueryClient()
  const { isLoadingStudent, student } = useGetStudent()
  const { school } = useGetSchool()
  const { updateStudent } = useUpdateStudent()
  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()
  const { applicationPageProps } = useGetApplicationPageProps()
  const [removedEHIds, setRemovedEHIds] = useState<string[]>([])
  const gradeSchemes = applicationPageProps?.gradeSchemes || []
  const aiProcessDocumentEnabled =
    applicationPageProps?.specificFields?.educationHistoryDocumentTypes?.documentTypes?.transcript
      ?.AiProcessDocumentEnabled ?? true

  useEffect(() => {
    resetFiles(
      getFilesOfType(
        [DocumentTags.TRANSCRIPTS, DocumentTags.CERTIFICATE],
        props.application?.attributes?.files as FileData,
        { activeFiles: true },
      ),
    )
  }, [props.application?.attributes?.files, resetFiles])

  if (isLoadingStudent) {
    return <Loading />
  }

  return (
    <Form
      id={props.formId}
      defaultValues={setDefaultEducationHistory(
        props.application?.attributes?.educationHistory,
        props.application?.attributes?.files,
        student,
      )}
      onSubmit={async data => {
        if (props.disabled) {
          props.onSuccess()
        } else {
          const edHist: SetAllFieldsOptional<EducationHistory> = {}

          for (const formValues of data.educationHistory) {
            edHist[formValues.id] = {
              educationLevel:
                (formValues.educationLevel && (formValues.educationLevel as LevelOfEducation)) ||
                undefined,
              educationCountry:
                (formValues.country && (formValues.country as CountryIsoCode)) || undefined,
              credentialReceived: formValues?.credentialReceived || '',
              schoolName: formValues?.schoolName || '',
              startDate: formValues?.startDate?.substring(0, 7),
              endDate: formValues?.endDate?.substring(0, 7),
            }

            if (formValues.gradeSchemeId) {
              const gradeSchemeName =
                gradeSchemes.find(scheme => scheme.id === formValues.gradeSchemeId)?.name || ''
              ;(edHist[formValues.id] as Record<string, unknown>).gradeSchemeId =
                formValues.gradeSchemeId
              if (gradeSchemeName) {
                ;(edHist[formValues.id] as Record<string, unknown>).gradeSchemeName =
                  gradeSchemeName
              }
            }
          }

          if (props.application?.attributes?.educationHistory) {
            const initialEducationHistoryIds = Object.keys(
              props.application?.attributes?.educationHistory,
            )
            const edHistIdsToRemove = initialEducationHistoryIds.filter(id => {
              return !edHist[id]
            })
            if (edHistIdsToRemove.length) {
              for (const id of edHistIdsToRemove) {
                edHist[id] = null
              }
            }
          }

          try {
            const updateApplicationPromise = new Promise<RawApplicationResponse>(
              (resolve, reject) => {
                props.updateApplication(
                  {
                    attributes: {
                      educationHistory: edHist,
                    },
                    files: pendingFileUploadState,
                  },
                  {
                    onSuccess: response => {
                      resetFiles(
                        getFilesOfType(
                          [DocumentTags.PASSPORT, DocumentTags.DESTINATION_COUNTRY_STATUS],
                          response.data?.attributes?.files as FileData,
                        ),
                      )

                      resolve(response)
                    },
                    onError: error =>
                      reject(error instanceof Error ? error : new Error(String(error))),
                  },
                )
              },
            )

            const updateStudentPromise = student?.id
              ? new Promise((resolve, reject) => {
                  const schoolInformation: StudentSchoolInformation = {
                    haveAttendedSchool: convertYesNoEmptyToBoolOrNull(data.haveAttendedSchool),
                    ontarioEducationNumber: data.ontarioEducationNumber
                      ? Number.parseInt(data.ontarioEducationNumber)
                      : null,
                    albertaStudentNumber: data.albertaStudentNumber
                      ? Number.parseInt(data.albertaStudentNumber)
                      : null,
                    personalEducationNumber: data.personalEducationNumber
                      ? Number.parseInt(data.personalEducationNumber)
                      : null,
                  }

                  if (data.haveAttendedSchool === YesNo.YES) {
                    set(
                      schoolInformation,
                      'schoolAssignedStudentId',
                      data.schoolAssignedStudentId || null,
                    )
                  }

                  updateStudent(
                    {
                      id: student.id,
                      attributes: {
                        schoolInformation,
                      },
                    },
                    {
                      onSuccess: studentResponse => {
                        queryClient.setQueryData(['students'], studentResponse)
                        resolve(studentResponse)
                      },
                      onError: error =>
                        reject(error instanceof Error ? error : new Error(String(error))),
                    },
                  )
                })
              : Promise.resolve(null)

            const [appResponse] = await Promise.all([
              updateApplicationPromise,
              updateStudentPromise,
            ])

            props.onSuccess(appResponse)
          } catch (error) {
            props.onError(error as GenericError)
            return
          }
        }
      }}
    >
      <EducationHistoryItems
        disabled={props.disabled}
        application={props.application}
        setRemovedEHIds={(id: string) => setRemovedEHIds([...removedEHIds, id])}
        gradeSchemes={gradeSchemes}
        aiProcessDocumentEnabled={aiProcessDocumentEnabled}
      />
      <RelevantStudentInformationFields
        disabled={props.disabled}
        student={student}
        schoolName={school?.attributes?.name}
      />
    </Form>
  )
}

type EducationHistoryItemsProps = Readonly<{
  disabled?: boolean
  application: StudentApplication
  setRemovedEHIds: (id: string) => void
  gradeSchemes: Array<{ id: string; name: string }>
  aiProcessDocumentEnabled: boolean
}>

function EducationHistoryItems(props: EducationHistoryItemsProps) {
  const setFieldValues = useSetFieldValues()
  const { fields, append, remove } = useFieldArray({ name: 'educationHistory' })
  const { educationHistory } = useFieldValues(['educationHistory'])
  const { addPendingDelete, getObservableFiles } = useApplicationFormContext()

  const maxDate = new Date()
  const minDate = new Date()

  maxDate.setFullYear(maxDate.getFullYear() + 15)
  minDate.setFullYear(minDate.getFullYear() - 50)

  const addButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <Flex direction="column" gap={4}>
        <Text variant="bodyM">
          Please provide your education history as per the program requirements.
        </Text>
        <Notice intent="secondary">
          <Flex direction={{ xs: 'column', md: 'row' }} gap={4}>
            <Text variant={{ xs: 'bodyL', md: 'bodyM' }}>
              <strong>Official Transcripts Required:</strong> You may upload copies, but we still
              require official transcripts to be sent from the issuing institution. Please be sure
              to follow the instructions we provide for official international transcript
              submission.
            </Text>
          </Flex>
        </Notice>
        {fields.map((field, index) => {
          if (index >= educationHistory.length) return null

          const pendingTranscriptUploads = Object.values(
            getObservableFiles({
              fileType: DocumentTags.TRANSCRIPTS,
              sectionReference: educationHistory[index].id,
            }),
          )
          const requiresGradeScheme =
            educationHistory[index].transcriptDocuments.length > 0 ||
            pendingTranscriptUploads.length > 0

          return (
            <Flex direction="column" gap={4} key={field.id}>
              <Text variant="titleS">{`Education Experience ${index + 1}`}</Text>
              <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={Select}
                    label="Education level"
                    name={`educationHistory.${index}.educationLevel`}
                    appearance="styled"
                    disabled={props.disabled}
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Education level is required'
                        : false
                    }
                  >
                    <Select.Option
                      value={EducationLevel.PRIMARY_SCHOOL}
                      label="Elementary / Primary"
                    />
                    <Select.Option
                      value={EducationLevel.MIDDLE_SCHOOL}
                      label="Junior Secondary / Middle School"
                    />
                    <Select.Option
                      value={EducationLevel.HIGH_SCHOOL}
                      label="Senior Secondary / High School"
                    />
                    <Select.Option value={EducationLevel.UNDERGRAD} label="Undergraduate" />
                    <Select.Option value={EducationLevel.GRADUATE} label="Graduate" />
                    <Select.Option value={EducationLevel.PHD} label="Doctorate" />
                  </Field>
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={Combobox}
                    label="Education country"
                    name={`educationHistory.${index}.country`}
                    size="md"
                    placeholder="Select"
                    disabled={props.disabled}
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Education country is required'
                        : false
                    }
                  >
                    {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                      <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
                    ))}
                  </Field>
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Name of institution"
                    name={`educationHistory.${index}.schoolName`}
                    disabled={props.disabled}
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Name of institution is required'
                        : false
                    }
                  />
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Credential gained (or expected)"
                    name={`educationHistory.${index}.credentialReceived`}
                    disabled={props.disabled}
                    helpText="E.g. Bachelors of Applied Science in Civil Engineering or High School Diploma"
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Credential gained is required'
                        : false
                    }
                  />
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={DatePicker}
                    label="Education start"
                    name={`educationHistory.${index}.startDate`}
                    maxDate={maxDate.toISOString()}
                    minDate={minDate.toISOString()}
                    granularity={'month'}
                    disabled={props.disabled}
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Education start date is required'
                        : false
                    }
                    validate={date => {
                      const startDate = new Date(date)
                      const endDate = new Date(educationHistory[index].endDate)
                      if (endDate && startDate >= endDate) {
                        return 'Education start date must be before end date'
                      }

                      return true
                    }}
                  />
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={DatePicker}
                    label="Education end (or expected)"
                    name={`educationHistory.${index}.endDate`}
                    maxDate={maxDate.toISOString()}
                    minDate={minDate.toISOString()}
                    granularity={'month'}
                    disabled={props.disabled}
                    required={
                      isFieldRequired({ disabled: props.disabled })
                        ? 'Education end date is required'
                        : false
                    }
                    validate={date => {
                      const endDate = new Date(date)
                      const startDate = new Date(educationHistory[index].startDate)
                      if (startDate && startDate >= endDate) {
                        return 'Education end date must be after start date'
                      }

                      return true
                    }}
                  />
                </Flex.Item>
                {props.aiProcessDocumentEnabled ? (
                  <Flex.Item basis={'100%'}>
                    <Field
                      as={Select}
                      appearance="styled"
                      label="Transcript grade scheme"
                      name={`educationHistory.${index}.gradeSchemeId`}
                      placeholder={props.gradeSchemes.length > 0 ? 'Select' : 'Not available'}
                      disabled={props.disabled || props.gradeSchemes.length === 0}
                      required={
                        requiresGradeScheme && props.gradeSchemes.length > 0
                          ? 'Grade scheme is required when a transcript is uploaded.'
                          : false
                      }
                      validate={value => {
                        if (requiresGradeScheme && props.gradeSchemes.length > 0 && !value) {
                          return 'Please select a grade scheme for the transcript.'
                        }
                        return true
                      }}
                      helpText={
                        props.gradeSchemes.length === 0
                          ? 'No grade schemes are available for this school.'
                          : undefined
                      }
                    >
                      {props.gradeSchemes.map(scheme => (
                        <Select.Option key={scheme.id} label={scheme.name} value={scheme.id} />
                      ))}
                    </Field>
                  </Flex.Item>
                ) : null}
                <Flex>
                  <Text variant={'bodyM'}>
                    Add your supporting document(s) below (at least one is required). Supported file
                    formats: PDF, JPEG, PNG <Asterisk />
                  </Text>
                </Flex>
                <Field
                  as={FileUploadField}
                  allowedFileTypes={['.jpg', '.jpeg', '.pdf', '.png']}
                  application={props.application}
                  disabled={props.disabled}
                  fileLimit={UPLOAD_LIMIT}
                  fileType={DocumentTags.TRANSCRIPTS}
                  label={
                    <Flex direction={'column'} gap={1}>
                      <Text variant={'labelM'}>Document type</Text>
                      <Text variant={'bodyM'}>Transcript</Text>
                    </Flex>
                  }
                  name={`educationHistory.${index}.transcriptDocuments`}
                  onRemove={(id: string) => {
                    const newEducationHistory = [...educationHistory]
                    newEducationHistory[index].transcriptDocuments = newEducationHistory[
                      index
                    ].transcriptDocuments.filter(file => file.id !== id)
                    setFieldValues({ educationHistory: newEducationHistory })
                  }}
                  section={educationHistory[index].id}
                  showHistory={!!props.disabled}
                  validate={value => {
                    const observableFiles = getObservableFiles({
                      fileType: DocumentTags.TRANSCRIPTS,
                      sectionReference: educationHistory[index].id,
                    })

                    const transcriptHasValue =
                      !!value.length || !!Object.keys(observableFiles).length
                    const certificateHasValue =
                      !!educationHistory[index].certificateDocuments.length ||
                      !!Object.keys(
                        getObservableFiles({
                          fileType: DocumentTags.CERTIFICATE,
                          sectionReference: educationHistory[index].id,
                        }),
                      ).length

                    if (Object.keys(observableFiles).length > UPLOAD_LIMIT) {
                      return `This field has a file limit of ${UPLOAD_LIMIT}.`
                    }

                    if (!(transcriptHasValue || certificateHasValue)) {
                      return 'At least one transcript or certificate must be attached.'
                    }
                    return true
                  }}
                />
                <Field
                  as={FileUploadField}
                  allowedFileTypes={['.jpg', '.jpeg', '.pdf', '.png']}
                  application={props.application}
                  disabled={props.disabled}
                  fileLimit={UPLOAD_LIMIT}
                  fileType={DocumentTags.CERTIFICATE}
                  label={
                    <Flex direction={'column'} gap={1}>
                      <Text variant={'labelM'}>Document type</Text>
                      <Text variant={'bodyM'}>Certificate</Text>
                    </Flex>
                  }
                  name={`educationHistory.${index}.certificateDocuments`}
                  onRemove={(id: string) => {
                    const newEducationHistory = [...educationHistory]
                    newEducationHistory[index].certificateDocuments = newEducationHistory[
                      index
                    ].certificateDocuments.filter(file => file.id !== id)
                    setFieldValues({ educationHistory: newEducationHistory })
                  }}
                  section={educationHistory[index].id}
                  showHistory={!!props.disabled}
                  validate={value => {
                    const observableFiles = getObservableFiles({
                      fileType: DocumentTags.CERTIFICATE,
                      sectionReference: educationHistory[index].id,
                    })

                    const certificateHasValue =
                      !!value.length || !!Object.keys(observableFiles).length
                    const transcriptHasValue =
                      !!educationHistory[index].transcriptDocuments.length ||
                      !!Object.keys(
                        getObservableFiles({
                          fileType: DocumentTags.TRANSCRIPTS,
                          sectionReference: educationHistory[index].id,
                        }),
                      ).length

                    if (Object.keys(observableFiles).length > UPLOAD_LIMIT) {
                      return `This field has a file limit of ${UPLOAD_LIMIT}.`
                    }

                    if (!(transcriptHasValue || certificateHasValue)) {
                      return 'At least one transcript or certificate must be attached.'
                    }
                    return true
                  }}
                />
              </Flex>
              {!props.disabled && Object.keys(educationHistory).length > 1 ? (
                <Flex>
                  <Button
                    emphasis="highlighted"
                    intent="negative"
                    width="fill"
                    leadIcon={DeleteOutlineIcon}
                    onClick={() => {
                      const transcriptFiles = Object.keys(
                        getObservableFiles({
                          fileType: DocumentTags.TRANSCRIPTS,
                          sectionReference: educationHistory[index].id,
                        }),
                      )
                      for (const id of transcriptFiles) {
                        addPendingDelete(id)
                      }
                      const certificateFiles = Object.keys(
                        getObservableFiles({
                          fileType: DocumentTags.CERTIFICATE,
                          sectionReference: educationHistory[index].id,
                        }),
                      )
                      for (const id of certificateFiles) {
                        addPendingDelete(id)
                      }
                      remove(index)
                      if (addButtonRef.current) {
                        addButtonRef.current.focus()
                      }
                    }}
                  >
                    {`Remove Education Experience ${index + 1}`}
                  </Button>
                </Flex>
              ) : null}
              <Divider />
            </Flex>
          )
        })}
      </Flex>
      {props.disabled ? null : (
        <Flex>
          <Button
            emphasis="highlighted"
            intent="primary"
            width="fill"
            leadIcon={PlusOutlineIcon}
            ref={addButtonRef}
            onClick={() => {
              append(newEducationHistory())
            }}
          >
            Add another education experience
          </Button>
        </Flex>
      )}
    </>
  )
}

type RelevantStudentInformationFieldsProps = Readonly<{
  disabled?: boolean
  student?: Student
  schoolName?: string
}>

function RelevantStudentInformationFields(props: Readonly<RelevantStudentInformationFieldsProps>) {
  const { applicationPageProps } = useGetApplicationPageProps()
  const { isSpecificFieldRequired } = specificFieldValidations(applicationPageProps?.specificFields)

  const { haveAttendedSchool } = useFieldValues(['haveAttendedSchool'])

  const haveAttendedSchoolEnabled = get(
    applicationPageProps,
    `specificFields.schoolInformation.haveAttendedSchool.enabled`,
  )
  const ontarioEducationNumberEnabled = get(
    applicationPageProps,
    `specificFields.schoolInformation.ontarioEducationNumber.enabled`,
  )
  const albertaStudentNumberEnabled = get(
    applicationPageProps,
    `specificFields.schoolInformation.albertaStudentNumber.enabled`,
  )
  const personalEducationNumberEnabled = get(
    applicationPageProps,
    `specificFields.schoolInformation.personalEducationNumber.enabled`,
  )

  if (
    !haveAttendedSchoolEnabled &&
    !ontarioEducationNumberEnabled &&
    !albertaStudentNumberEnabled &&
    !personalEducationNumberEnabled &&
    typeof props.student?.attributes?.schoolInformation?.haveAttendedSchool !== 'boolean' &&
    !props.student?.attributes?.schoolInformation?.ontarioEducationNumber &&
    !props.student?.attributes?.schoolInformation?.albertaStudentNumber &&
    !props.student?.attributes?.schoolInformation?.personalEducationNumber
  ) {
    return null
  }

  const haveAttendedSchoolLabel = props.schoolName
    ? `Have you studied at ${props.schoolName} before?`
    : 'Have you previously attended our institution?'

  return (
    <>
      <Flex direction={{ xs: 'column', sm: 'row' }} gap={4} py={4} wrap>
        <Flex.Item basis="100%">
          <Text variant="labelL">Relevant student information</Text>
        </Flex.Item>
        <SpecificField
          field="schoolInformation.haveAttendedSchool"
          forceEnabled={
            typeof props.student?.attributes?.schoolInformation?.haveAttendedSchool === 'boolean'
          }
        >
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            <Field
              as={Select}
              label={haveAttendedSchoolLabel}
              name="haveAttendedSchool"
              appearance="styled"
              placeholder="Select"
              disabled={props.disabled}
              required={
                !props.disabled && isSpecificFieldRequired('schoolInformation.haveAttendedSchool')
                  ? `"${haveAttendedSchoolLabel}" is required`
                  : false
              }
            >
              <Select.Option value="yes" label="Yes" />
              <Select.Option value="no" label="No" />
            </Field>
          </Flex.Item>
          {haveAttendedSchool === YesNo.YES ? (
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Student ID"
                name="schoolAssignedStudentId"
                disabled={props.disabled}
                required={
                  !props.disabled &&
                  haveAttendedSchool === YesNo.YES &&
                  isSpecificFieldRequired('schoolInformation.schoolAssignedStudentId')
                    ? 'Student ID is required'
                    : false
                }
              />
            </Flex.Item>
          ) : null}
        </SpecificField>
      </Flex>
      <Flex direction={{ xs: 'column', sm: 'row' }} gap={4} wrap>
        <SpecificField
          field="schoolInformation.ontarioEducationNumber"
          forceEnabled={!!props.student?.attributes?.schoolInformation?.ontarioEducationNumber}
        >
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            <Field
              as={TextInput as ComponentType<TextInputProps>}
              label="OEN (Ontario Education Number)"
              name="ontarioEducationNumber"
              disabled={props.disabled}
              required={
                !props.disabled &&
                isSpecificFieldRequired('schoolInformation.ontarioEducationNumber')
                  ? 'OEN (Ontario Education Number) is required'
                  : false
              }
            />
          </Flex.Item>
        </SpecificField>
        <SpecificField
          field="schoolInformation.albertaStudentNumber"
          forceEnabled={!!props.student?.attributes?.schoolInformation?.albertaStudentNumber}
        >
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            <Field
              as={TextInput as ComponentType<TextInputProps>}
              label="ASN (Alberta Student Number)"
              name="albertaStudentNumber"
              disabled={props.disabled}
              required={
                !props.disabled && isSpecificFieldRequired('schoolInformation.albertaStudentNumber')
                  ? 'ASN (Alberta Student Number) is required'
                  : false
              }
            />
            <Flex gap={2} align="center" direction="row">
              <HelpText intent="secondary" showIcon={true}>
                If you already have an ASN but don’t remember it, you can{' '}
                <Link
                  href="https://learnerregistry.ae.alberta.ca/home/startlookup"
                  variant="inline"
                >
                  look it up.
                </Link>
                <br />
                If you do not have an ASN, please{' '}
                <Link
                  href="https://learnerregistry.ae.alberta.ca/Home/StartAsnRequest"
                  variant="inline"
                >
                  create one
                </Link>{' '}
                before continuing.
              </HelpText>
            </Flex>
          </Flex.Item>
        </SpecificField>
        <SpecificField
          field="schoolInformation.personalEducationNumber"
          forceEnabled={!!props.student?.attributes?.schoolInformation?.personalEducationNumber}
        >
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            <Field
              as={TextInput as ComponentType<TextInputProps>}
              label="PEN (Personal Education Number)"
              name="personalEducationNumber"
              disabled={props.disabled}
              required={
                !props.disabled &&
                isSpecificFieldRequired('schoolInformation.personalEducationNumber')
                  ? 'PEN (Personal Education Number) is required'
                  : false
              }
            />
          </Flex.Item>
        </SpecificField>
      </Flex>
    </>
  )
}

function isFieldRequired(params: { disabled?: boolean }) {
  if (params.disabled) {
    return false
  }

  return true
}

function newEducationHistory(): EducationalAccomplishment {
  return {
    id: nanoid(),
    educationLevel: '',
    credentialReceived: '',
    schoolName: '',
    country: '',
    startDate: '',
    endDate: '',
    gradeSchemeId: '',
    transcriptDocuments: [],
    certificateDocuments: [],
  }
}

function setDefaultEducationHistory(
  educationItems?: SetAllFieldsOptional<EducationHistory>,
  files?: SetAllFieldsOptional<FileData>,
  student?: Student,
): EducationHistoryFormFields {
  const schoolInformation = student?.attributes?.schoolInformation

  const formObject: EducationHistoryFormFields = {
    haveAttendedSchool: convertBooleanToYesNoEmpty(schoolInformation?.haveAttendedSchool),
    schoolAssignedStudentId: schoolInformation?.schoolAssignedStudentId ?? '',
    ontarioEducationNumber: schoolInformation?.ontarioEducationNumber?.toString() ?? '',
    albertaStudentNumber: schoolInformation?.albertaStudentNumber?.toString() ?? '',
    personalEducationNumber: schoolInformation?.personalEducationNumber?.toString() ?? '',
    educationHistory: [],
  }

  for (const [id, ehi] of Object.entries(educationItems || [])) {
    let gradeSchemeId = ''
    const transcriptIds = ehi?.transcripts || []
    if (transcriptIds.length > 0) {
      const latestTranscriptId = transcriptIds[transcriptIds.length - 1]
      if (latestTranscriptId) {
        const transcriptMetadata = files?.[latestTranscriptId] as
          | Record<string, unknown>
          | undefined
        gradeSchemeId = (transcriptMetadata?.gradeSchemeId as string) ?? ''
      }
    }

    formObject.educationHistory.push({
      id: id,
      educationLevel: ehi?.educationLevel ?? '',
      country: ehi?.educationCountry ?? '',
      schoolName: ehi?.schoolName ?? '',
      credentialReceived: ehi?.credentialReceived ?? '',
      startDate: ehi?.startDate
        ? convertTimelessDateStrToLocalDate(ehi.startDate).toISOString()
        : '',
      endDate: ehi?.endDate ? convertTimelessDateStrToLocalDate(ehi.endDate).toISOString() : '',
      gradeSchemeId,
      certificateDocuments: [],
      transcriptDocuments: [],
    })
  }

  if (isEmpty(formObject.educationHistory)) {
    formObject.educationHistory = [newEducationHistory()]
  } else {
    formObject.educationHistory.sort(
      (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime(),
    )
  }

  return formObject
}
